import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Clock, LogOut, RefreshCw, XCircle } from "lucide-react";

export default function PendingApproval() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();

  // As soon as the admin approves, send the student to their dashboard.
  useEffect(() => {
    if (user?.role === "student" && user?.status === "approved") {
      navigate("/app", { replace: true });
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const rejected = user?.status === "rejected";
  const suspended = user?.status === "suspended";

  const cfg = rejected
    ? { icon: XCircle, color: "text-destructive", title: "Enrollment rejected", msg: "Your enrollment request was not approved. Please contact your institution administrator." }
    : suspended
    ? { icon: XCircle, color: "text-destructive", title: "Account suspended", msg: "Your account has been suspended. Please contact your administrator." }
    : { icon: Clock, color: "text-amber-500", title: "Pending approval", msg: "Your registration is under review. Once an administrator approves your enrollment, you'll be taken straight to your dashboard." };

  const Icon = cfg.icon;

  return (
    <div className="min-h-screen flex flex-col bg-secondary/40">
      <header className="h-16 bg-card border-b border-border flex items-center px-6">
        <Brand />
      </header>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bg-card border border-border max-w-lg w-full p-10 text-center animate-fade-up" data-testid="pending-card">
          <div className="mx-auto h-16 w-16 border border-border flex items-center justify-center">
            <Icon className={`h-8 w-8 ${cfg.color}`} strokeWidth={1.5} />
          </div>
          <h1 className="mt-6 font-display text-2xl font-bold">{cfg.title}</h1>
          <p className="mt-3 text-muted-foreground">{cfg.msg}</p>

          <div className="mt-8 border border-border bg-card p-4 text-left">
            <div className="overline">Registration No.</div>
            <div className="font-mono text-sm mt-1">{user?.registration_number}</div>
          </div>

          <div className="mt-8 flex gap-3 justify-center">
            {!rejected && !suspended && (
              <Button variant="outline" className="rounded-sm" onClick={refresh} data-testid="pending-refresh">
                <RefreshCw className="h-4 w-4 mr-2" /> Check approval status
              </Button>
            )}
            <Button variant="ghost" className="rounded-sm" onClick={handleLogout} data-testid="pending-logout">
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
